import React, { useEffect, useState, useRef } from 'react';
import { Dropdown } from '@/components/ui/dropdown';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2, Share2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@/components/ui/input';
import { PageLoader } from '@/components/ui/page-loader';

interface MasterPlatform {
    _id: string;
    title: string;
    platformType: 'social-media' | 'audio-streaming';
    image: string;
    baseURL: string;
    isActive: boolean;
    order: number;
    createdAt: string;
    updatedAt: string;
}

const emptyPlatform = {
    title: '',
    platformType: 'social-media' as 'social-media' | 'audio-streaming',
    image: '',
    baseURL: '',
    isActive: true,
};

interface SortableRowProps {
    platform: MasterPlatform;
    onEdit: (platform: MasterPlatform) => void;
    onDelete: (id: string) => void;
    onToggleActive: (id: string, currentStatus: boolean) => void;
    togglingId: string | null;
}

function SortableRow({ platform, onEdit, onDelete, onToggleActive, togglingId }: SortableRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: platform._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            className="transition-colors"
        >
            <TableCell className="w-10 select-none cursor-grab">
                <div className="flex items-center gap-2" {...attributes} {...listeners}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                        <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
                        <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
                    </svg>
                    <span className="w-4 text-muted-foreground font-medium">{platform.order + 1}</span>
                </div>
            </TableCell>
            <TableCell>
                <div 
                    className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary/50 p-2"
                    style={{
                        backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 4px 4px',
                        backgroundRepeat: 'repeat'
                    }}
                >
                    <img
                        src={platform.image}
                        alt={platform.title}
                        className="w-full h-full object-contain"
                    />
                </div>
            </TableCell>
            <TableCell className="font-semibold">{platform.title}</TableCell>
            <TableCell>
                <span className={`px-2 py-1 rounded text-xs font-medium inline-block ${
                    platform.platformType === 'social-media'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                }`}>
                    {platform.platformType === 'social-media' ? 'Social Media' : 'Audio Streaming'}
                </span>
            </TableCell>
            <TableCell className="font-mono text-sm text-muted-foreground max-w-xs truncate">
                {platform.baseURL}
            </TableCell>
            <TableCell>
                <div className="text-xs text-muted-foreground">
                    {new Date(platform.createdAt).toLocaleDateString()}
                </div>
            </TableCell>
            <TableCell>
                <div className="flex items-center space-x-2">
                    <Switch
                        checked={platform.isActive}
                        onCheckedChange={() => onToggleActive(platform._id, platform.isActive)}
                        aria-label="Toggle platform active state"
                        disabled={togglingId === platform._id}
                    />
                    <span className={platform.isActive ? 'status-label-active' : 'status-label-inactive'}>
                        {platform.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </TableCell>
            <TableCell>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => onEdit(platform)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </Button>
                    <Button
                        variant="destructive"
                        size="icon-sm"
                        onClick={() => onDelete(platform._id)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

const MasterPlatformsManager: React.FC = () => {
    const [platforms, setPlatforms] = useState<MasterPlatform[]>([]);
    const [form, setForm] = useState(emptyPlatform);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [uploadStep, setUploadStep] = useState<'idle' | 'cropping'>('idle');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [urlSuggestion, setUrlSuggestion] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cropperRef = useRef<any>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchPlatforms = () => {
        setLoading(true);
        axios
            .get(`${BASE_URL}/api/master-platform`)
            .then((res) => setPlatforms(res.data))
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Failed to fetch platforms');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchPlatforms();
    }, []);

    const resetUploadState = () => {
        setUploadStep('idle');
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const openFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const normalizeUrl = (input: string): { url: string; suggestion: string | null } => {
        let normalized = input.trim();
        let suggestion = null;

        if (!normalized) return { url: '', suggestion: null };

        // Remove existing protocol if present
        normalized = normalized.replace(/^https?:\/\//, '');

        // Add https:// protocol
        const url = `https://${normalized}`;

        // Check if user entered protocol (we removed it)
        if (input.match(/^https?:\/\//)) {
            suggestion = 'Protocol removed and normalized to https://';
        }

        // Check for common issues
        if (normalized.includes(' ')) {
            suggestion = 'Warning: URL contains spaces';
        } else if (!normalized.includes('.')) {
            suggestion = 'Warning: URL might be incomplete (no domain extension)';
        }

        return { url, suggestion };
    };

    const openCropperForExisting = () => {
        if (form.image) {
            fetch(form.image)
                .then(res => res.blob())
                .then(blob => {
                    // Check if it's an SVG
                    if (blob.type === 'image/svg+xml' || form.image.endsWith('.svg')) {
                        toast.info('SVG files cannot be edited. Please upload a new file.');
                        return;
                    }
                    const file = new File([blob], 'platform-icon.png', { type: blob.type });
                    setSelectedFile(file);
                    setUploadStep('cropping');
                })
                .catch(() => {
                    toast.error('Failed to load image for editing');
                });
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validFormats = ['image/svg+xml', 'image/png', 'image/jpeg'];
        const maxSize = 2 * 1024 * 1024;

        if (!validFormats.includes(file.type)) {
            setError('Only .svg, .png and .jpeg files are allowed.');
            toast.error('Only .svg, .png and .jpeg files are allowed.');
            return;
        }

        if (file.size > maxSize) {
            setError('File size must be less than 2MB.');
            toast.error('File size must be less than 2MB.');
            return;
        }

        setError('');
        
        // SVG files don't need cropping - upload directly
        if (file.type === 'image/svg+xml') {
            setUploading(true);
            try {
                const formData = new FormData();
                formData.append('file', file);

                const res = await axios.post(BASE_URL + '/api/images/upload', formData);
                setForm((prev) => ({ ...prev, image: res.data.url }));
                const isUpdate = form.image ? true : false;
                toast.success(isUpdate ? 'Icon updated successfully!' : 'Icon uploaded successfully!');
            } catch (err) {
                setError('Icon upload failed.');
                toast.error('Icon upload failed.');
            } finally {
                setUploading(false);
            }
        } else {
            // PNG and JPEG files go through cropping
            setSelectedFile(file);
            setUploadStep('cropping');
        }
    };

    const handleCropComplete = async (croppedImage: string) => {
        if (!croppedImage) return;

        setUploading(true);
        try {
            const response = await fetch(croppedImage);
            const blob = await response.blob();

            const formData = new FormData();
            formData.append('file', blob, 'cropped-platform-icon.png');

            const res = await axios.post(BASE_URL + '/api/images/upload', formData);
            setForm((prev) => ({ ...prev, image: res.data.url }));
            resetUploadState();
            const isUpdate = form.image ? true : false;
            toast.success(isUpdate ? 'Icon updated successfully!' : 'Icon uploaded successfully!');
        } catch (err) {
            setError('Icon upload failed.');
            toast.error('Icon upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        // Special handling for baseURL field
        if (name === 'baseURL') {
            const { url, suggestion } = normalizeUrl(value);
            setForm((prev) => ({ ...prev, [name]: url }));
            setUrlSuggestion(suggestion);
            if (suggestion) {
                toast.info(suggestion, { duration: 4000 });
            }
        } else {
            setForm((prev) => ({
                ...prev,
                [name]: value,
            }));
            setUrlSuggestion(null);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        if (!form.image) {
            setError('Please upload an icon');
            toast.error('Please upload an icon');
            return;
        }

        if (!form.baseURL) {
            setError('Please enter a base URL');
            toast.error('Please enter a base URL');
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/master-platform`, form);
            setAddOpen(false);
            setForm(emptyPlatform);
            resetUploadState();
            toast.success('Platform added successfully!');
            if (response.data) {
                setPlatforms(prev => [...prev, response.data]);
            } else {
                fetchPlatforms();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to add platform';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item: MasterPlatform) => {
        setEditId(item._id);
        setForm({
            title: item.title,
            platformType: item.platformType,
            image: item.image,
            baseURL: item.baseURL,
            isActive: item.isActive,
        });
        setEditOpen(true);
        setError('');
        resetUploadState();
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        if (!form.image) {
            setError('Please upload an icon');
            toast.error('Please upload an icon');
            return;
        }

        if (!form.baseURL) {
            setError('Please enter a base URL');
            toast.error('Please enter a base URL');
            return;
        }

        setSaving(true);
        const currentEditId = editId;
        try {
            const response = await axios.put(`${BASE_URL}/api/master-platform/${editId}`, form);
            setEditOpen(false);
            setEditId(null);
            setForm(emptyPlatform);
            resetUploadState();
            toast.success('Platform updated successfully!');
            if (response.data) {
                setPlatforms(prev => prev.map(platform =>
                    platform._id === currentEditId ? { ...platform, ...response.data } : platform
                ));
            } else {
                fetchPlatforms();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update platform';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        const deletedPlatform = platforms.find(platform => platform._id === deleteId);
        // Optimistic removal
        setPlatforms(prev => prev.filter(platform => platform._id !== deleteId));
        setDeleteId(null);
        try {
            await axios.delete(`${BASE_URL}/api/master-platform/${deleteId}`);
            toast.success('Platform deleted successfully!');
        } catch (err: any) {
            // Revert on error
            if (deletedPlatform) {
                setPlatforms(prev => [...prev, deletedPlatform].sort((a, b) => a.order - b.order));
            }
            const errorMessage = err.response?.data?.message || 'Failed to delete platform';
            toast.error(errorMessage);
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        setTogglingId(id);
        // Optimistic update
        setPlatforms(prev => prev.map(platform =>
            platform._id === id ? { ...platform, isActive: !currentStatus } : platform
        ));
        try {
            await axios.patch(`${BASE_URL}/api/master-platform/${id}/toggle-active`, {});
            toast.success(`Platform ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
        } catch (err: any) {
            // Revert on error
            setPlatforms(prev => prev.map(platform =>
                platform._id === id ? { ...platform, isActive: currentStatus } : platform
            ));
            const errorMessage = err.response?.data?.message || 'Failed to update platform status';
            toast.error(errorMessage);
        } finally {
            setTogglingId(null);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = platforms.findIndex((item) => item._id === active.id);
            const newIndex = platforms.findIndex((item) => item._id === over.id);

            const newOrder = arrayMove(platforms, oldIndex, newIndex);
            setPlatforms(newOrder);

            try {
                await axios.put(`${BASE_URL}/api/master-platform/${active.id}/reorder`, {
                    newOrder: newIndex,
                });
                toast.success('Platform reordered successfully!');
            } catch (err: any) {
                toast.error('Failed to reorder platform');
                fetchPlatforms();
            }
        }
    };

    const renderImageUpload = () => (
        <div>
            <Label className="form-label">Platform Icon/Image (Square, SVG recommended)</Label>
            {uploading ? (
                <div className="w-full">
                    <Card className="flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 text-sm">
                        <Spinner size="xl" />
                        <span className="text-muted-foreground">Uploading icon...</span>
                    </Card>
                </div>
            ) : uploadStep === 'idle' ? (
                !form.image ? (
                    <div className="w-full">
                        <Card
                            className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-secondary/50 transition-colors"
                            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                                e.preventDefault();
                                e.currentTarget.classList.add('bg-muted/50');
                            }}
                            onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                                e.currentTarget.classList.remove('bg-muted/50');
                            }}
                            onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('bg-muted/50');
                                const files = e.dataTransfer.files;
                                if (files.length > 0) {
                                    const event = {
                                        target: { files }
                                    } as any;
                                    handleFileChange(event);
                                }
                            }}
                            onClick={openFileUpload}
                        >
                            <div className="grid space-y-3">
                                <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                                    <Upload className="size-5" />
                                    <div>
                                        Drop icon here or{" "}
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="text-primary p-0 h-auto font-normal"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openFileUpload();
                                            }}
                                        >
                                            browse
                                        </Button>{" "}
                                        to upload
                                    </div>
                                </div>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".svg,.png,.jpg,.jpeg"
                                onChange={handleFileChange}
                            />
                            <span className="text-xs text-muted-foreground mt-1 block">
                                Supported: SVG, JPG, PNG (max 2 MB) | Aspect ratio: 1:1
                            </span>
                        </Card>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Card
                            className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={openCropperForExisting}
                        >
                            <div 
                                className="relative w-32 h-32 rounded overflow-hidden"
                                style={{
                                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                                    backgroundSize: '16px 16px',
                                    backgroundPosition: '0 0, 8px 8px',
                                    backgroundRepeat: 'repeat'
                                }}
                            >
                                <img 
                                    src={form.image} 
                                    alt="uploaded" 
                                    className="w-full h-full object-contain p-4 cursor-pointer hover:opacity-80 transition-opacity" 
                                    title="Click to edit image"
                                />
                            </div>
                            <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                                <Upload className="size-4" />
                                <span>Click to edit or re-upload</span>
                            </div>
                        </Card>
                        <div className="flex gap-2">
                            <Button 
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={openFileUpload}
                            >
                                <RefreshCw className="size-3.5" />
                                Change
                            </Button>
                            <Button 
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-danger"
                                onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                            >
                                <Trash2 className="size-3.5" />
                                Remove
                            </Button>
                        </div>
                        <input
                            type="file"
                            accept=".svg,.png,.jpg,.jpeg"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            className="hidden"
                        />
                    </div>
                )
            ) : uploadStep === 'cropping' && selectedFile ? (
                <div className="space-y-2">
                    <Card className="flex flex-col items-center">
                        <div className="w-full border-b p-3">
                            <p className="text-sm font-medium text-foreground text-center">Adjust crop area (1:1 Aspect) - Drag to move, edges to resize</p>
                        </div>
                        <div className="w-full flex justify-center py-3">
                            <Cropper
                                file={selectedFile}
                                aspect={1}
                                maxImageSize={2 * 1024 * 1024}
                                onCrop={handleCropComplete}
                                onCropRef={cropperRef}
                            />
                        </div>
                    </Card>
                    <div className="flex gap-2">
                        <Button 
                            type="button"
                            onClick={async () => {
                                await cropperRef.current?.applyCrop();
                            }}
                            disabled={uploading}
                            size="sm"
                            loading={uploading}
                            loadingText="Uploading..."
                        >
                            {form.image ? 'Update' : 'Upload'}
                        </Button>
                        <Button 
                            type="button"
                            variant="outline"
                            onClick={resetUploadState}
                            disabled={uploading}
                            size="sm"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="page-title">Master Platform Management</h2>
                <Dialog open={addOpen} onOpenChange={(open) => {
                    setAddOpen(open);
                    if (!open) {
                        setForm(emptyPlatform);
                        resetUploadState();
                        setError('');
                        setUrlSuggestion(null);
                    }
                }}>
                    <DialogTrigger asChild>
                        <Button>Add Platform</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Add New Platform</DialogTitle>
                            <DialogDescription>Create a new platform for social media or audio streaming</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAdd} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium mb-1">Platform Title</label>
                                <Input
                                    name="title"
                                    placeholder="Enter platform title (e.g., Facebook, Spotify)"
                                    value={form.title}
                                    onChange={handleFormChange}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Platform Type</label>
                                <Dropdown
                                    options={[
                                        { value: 'social-media', label: 'Social Media' },
                                        { value: 'audio-streaming', label: 'Audio Streaming' },
                                    ]}
                                    value={form.platformType}
                                    onValueChange={(val) => setForm((prev) => ({ ...prev, platformType: val as typeof form.platformType }))}
                                    placeholder="Select Type"
                                />
                            </div>
                            {renderImageUpload()}

                            <div className="form-group">
                                <Label htmlFor="baseURL" className="form-label">Base URL</Label>
                                <Input 
                                    id="baseURL"
                                    name="baseURL" 
                                    value={(form.baseURL || '').replace(/^https?:\/\//, '')} 
                                    onChange={(e) => {
                                        const rawValue = e.target.value.replace(/^https?:\/\//, '');
                                        handleFormChange({ ...e, target: { ...e.target, name: 'baseURL', value: rawValue } } as any);
                                    }}
                                    required 
                                    prefix="https://"
                                    placeholder="e.g., facebook.com or spotify.com"
                                />
                                <div className="mt-2 text-xs space-y-1.5">
                                    <div className="text-muted-foreground">
                                        <strong className="font-semibold">Example:</strong> Enter <code className="bg-secondary px-1.5 py-0.5 rounded">facebook.com</code> → Saves as <code className="bg-secondary px-1.5 py-0.5 rounded">https://facebook.com</code>
                                    </div>
                                    {urlSuggestion && (
                                        <div className="bg-info-light border border-info/20 text-info p-2.5 rounded-lg">
                                            ℹ️ {urlSuggestion}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-3">
                                <Switch
                                    checked={form.isActive}
                                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                                    aria-label="Toggle active state"
                                />
                                <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                                    {form.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            {error && <div className="text-red-500 text-sm">{error}</div>}
                            <Button type="submit" loading={saving} loadingText="Adding...">Add Platform</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div>
                {loading ? (
                    <PageLoader label="Loading platforms..." />
                ) : platforms.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Share2 className="w-full h-full" />
                        </div>
                        <h3 className="empty-state-title">No platforms found</h3>
                        <p className="empty-state-description">Add your first social platform to get started</p>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead className="w-10">#</TableHead>
                                    <TableHead className="w-16">Icon</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Base URL</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <SortableContext
                                    items={platforms.map((p) => p._id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {platforms.map((platform) => (
                                        <SortableRow
                                            key={platform._id}
                                            platform={platform}
                                            onEdit={handleEdit}
                                            onDelete={setDeleteId}
                                            onToggleActive={handleToggleActive}
                                            togglingId={togglingId}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={(open) => {
                setEditOpen(open);
                if (!open) {
                    setEditId(null);
                    setForm(emptyPlatform);
                    resetUploadState();
                    setError('');
                    setUrlSuggestion(null);
                }
            }}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Platform</DialogTitle>
                        <DialogDescription>Update platform information</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium mb-1">Platform Title</label>
                            <Input
                                name="title"
                                placeholder="Enter platform title"
                                value={form.title}
                                onChange={handleFormChange}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Platform Type</label>
                            <Dropdown
                                options={[
                                    { value: 'social-media', label: 'Social Media' },
                                    { value: 'audio-streaming', label: 'Audio Streaming' },
                                ]}
                                value={form.platformType}
                                onValueChange={(val) => setForm((prev) => ({ ...prev, platformType: val as typeof form.platformType }))}
                                placeholder="Select Type"
                            />
                        </div>

                        {renderImageUpload()}

                        <div className="form-group">
                            <Label htmlFor="editBaseURL" className="form-label">Base URL</Label>
                            <Input 
                                id="editBaseURL"
                                name="baseURL" 
                                value={(form.baseURL || '').replace(/^https?:\/\//, '')} 
                                onChange={(e) => {
                                    const rawValue = e.target.value.replace(/^https?:\/\//, '');
                                    handleFormChange({ ...e, target: { ...e.target, name: 'baseURL', value: rawValue } } as any);
                                }}
                                required 
                                prefix="https://"
                                placeholder="e.g., facebook.com or spotify.com"
                            />
                            <div className="mt-2 text-xs space-y-1.5">
                                <div className="text-muted-foreground">
                                    <strong className="font-semibold">Example:</strong> Enter <code className="bg-secondary px-1.5 py-0.5 rounded">facebook.com</code> → Saves as <code className="bg-secondary px-1.5 py-0.5 rounded">https://facebook.com</code>
                                </div>
                                {urlSuggestion && (
                                    <div className="bg-info-light border border-info/20 text-info p-2.5 rounded-lg">
                                        ℹ️ {urlSuggestion}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Switch
                                checked={form.isActive}
                                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                                aria-label="Toggle active state"
                            />
                            <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                                {form.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <Button type="submit" loading={saving} loadingText="Saving...">Save Changes</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete Platform"
                description="Are you sure you want to delete this platform? This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default MasterPlatformsManager;
